import { pascalCase } from 'change-case';
import React, { useEffect, useState } from 'react';
import { toast } from '~/utils/toast';
import Button from '~/components/Button';
import Modal from '~/components/Modal';
import { AllowAccessStep } from '~/components/oauth/Steps';
import useExternalPopup from '~/components/oauth/useExternalPopup';
import Textarea from '~/components/Textarea';
import { SmartsheetProjectTypes } from '~/server/enums';
import { api } from '~/utils/api';
import { getProviderLogo } from '~/utils/logo';
import { serverError } from '~/utils/serverError';

import { RemoteTaskManagerProvider, type TaskManagerType } from '@prisma/client';

import { ConnectIconTile } from './ConnectIconTile';
import ProjectSelection from './ProjectSelection';
import { SmartsheetFieldMappingStep } from './SmartsheetFieldMappingStep';

export type TaskManagerModalProps = {
  provider: RemoteTaskManagerProvider | null;
  modalRef: React.RefObject<HTMLDialogElement | null>;
  closeModal: () => void;
  initialStep: OAuthSteps;
  /** Skip project selection after oauth */
  skipProjectSelection?: boolean;
  taskManagerType: TaskManagerType;
};

export enum OAuthSteps {
  ALLOW_ACCESS = 1,
  BEFORE_YOU_PROCEED = 2,
  PROJECT_SELECTION = 3,
  FIELD_MAPPING = 4,
}

export type Project = { key: string; name: string; disabled: boolean };

const permissions = {
  [RemoteTaskManagerProvider.ASANA]: [
    {
      title: 'Tickets',
      points: [
        'Assignees',
        'Attachments',
        'Collections',
        'Completed Timestamp',
        'Description',
        'Due Date',
        'Name',
        'Parent Ticket',
        'Remote Created Timestamp',
        'Remote Updated Timestamp',
        'Status',
        'Tags',
        'Ticket URL',
      ],
    },
    {
      title: 'Comments',
      points: ['Body', 'Html Body', 'Privacy', 'Remote Created Timestamp', 'Ticket', 'User'],
    },
    {
      title: 'Attachments',
      points: ['File Name', 'File URL', 'Remote Created Timestamp', 'Ticket'],
    },
    { title: 'Users', points: ['Avatar', 'Email Address', 'Name', 'Teams'] },
  ],
  [RemoteTaskManagerProvider.JIRA]: [
    {
      title: 'Tickets',
      points: [
        'Assignees',
        'Attachments',
        'Collections',
        'Completed Timestamp',
        'Contact',
        'Creator',
        'Description',
        'Due Date',
        'Name',
        'Parent Ticket',
        'Priority',
        'Project',
        'Remote Created Timestamp',
        'Remote Updated Timestamp',
        'Status',
        'Tags',
        'Ticket Type',
        'Ticket URL',
      ],
    },
    {
      title: 'Comments',
      points: ['Html Body', 'Privacy', 'Remote Created Timestamp', 'Ticket', 'User'],
    },
    {
      title: 'Attachments',
      points: [
        'Content Type',
        'File Name',
        'File URL',
        'Remote Created Timestamp',
        'Ticket',
        'Uploaded By',
      ],
    },
    { title: 'Users', points: ['Avatar', 'Email Address', 'Is Active', 'Name', 'Teams'] },
  ],
  [RemoteTaskManagerProvider.SMARTSHEET]: [
    {
      title: 'Tickets',
      points: [
        'Assignees',
        'Attachments',
        'Collections',
        'Completed Timestamp',
        'Description',
        'Due Date',
        'Name',
        'Parent Ticket',
        'Remote Created Timestamp',
        'Remote Updated Timestamp',
        'Status',
        'Tags',
        'Ticket URL',
      ],
    },
    {
      title: 'Comments',
      points: ['Body', 'Html Body', 'Privacy', 'Remote Created Timestamp', 'Ticket', 'User'],
    },
    {
      title: 'Attachments',
      points: ['File Name', 'File URL', 'Remote Created Timestamp', 'Ticket'],
    },
    { title: 'Users', points: ['Avatar', 'Email Address', 'Name', 'Teams'] },
  ],
  [RemoteTaskManagerProvider.WRIKE]: [
    {
      title: 'Tickets',
      points: [
        'Account',
        'Assignees',
        'Attachments',
        'Collections',
        'Completed Timestamp',
        'Description',
        'Due Date',
        'Name',
        'Parent Ticket',
        'Priority',
        'Project',
        'Remote Created Timestamp',
        'Remote Updated Timestamp',
        'Status',
        'Ticket URL',
      ],
    },
    {
      title: 'Comments',
      points: ['Body', 'Privacy', 'Remote Created Timestamp', 'Ticket', 'User'],
    },
    {
      title: 'Attachments',
      points: [
        'Content Type',
        'File Name',
        'File URL',
        'Remote Created Timestamp',
        'Ticket',
        'Uploaded By',
      ],
    },
    {
      title: 'Accounts',
      points: ['Name'],
    },
    {
      title: 'Users',
      points: ['Email Address', 'Is Active', 'Name', 'Teams'],
    },
    {
      title: 'Teams',
      points: ['Name'],
    },
    {
      title: 'Collections',
      points: ['Collection Type', 'Description', 'Name'],
    },
  ],
};

type BeforeYouProceedProps = Pick<TaskManagerModalProps, 'provider'> & {
  setExternalPopup: React.Dispatch<React.SetStateAction<Window | null>>;
  moveToProjectSelection: () => void;
  closeModal: () => void;
};

function ProviderMark({ provider }: { provider: RemoteTaskManagerProvider }) {
  return <ConnectIconTile src={getProviderLogo(provider).src} alt="" size="lg" />;
}

const BeforeYouProceed = ({
  provider,
  setExternalPopup,
  moveToProjectSelection,
  closeModal,
}: BeforeYouProceedProps) => {
  const { mutate: addApiToken, isLoading: isAddingApiToken } = api.oAuth.addApiToken.useMutation();
  const { isLoading, mutate: generateOAuthUrl } = api.oAuth.generateUrl.useMutation();
  const [token, setToken] = useState<string>('');
  const utils = api.useUtils();

  const openOAuthPopup = (url: string) => {
    const left = (window.screen.width - 800) / 2;
    const top = (window.screen.height - 1000) / 2;
    const features = `width=800,height=1000,toolbar=no,directories=no,status=no,menubar=no,scrollbars=no,resizable=no,left=${left},top=${top}`;
    const popup = window.open(url, 'popup', features);
    setExternalPopup(popup);
  };

  const onClickForOAuth = () => {
    generateOAuthUrl(
      {
        provider: provider!,
      },
      {
        onError: serverError,
        onSuccess: (url: string) => {
          openOAuthPopup(url);
        },
      },
    );
  };

  if (!provider) return;

  const providerName = pascalCase(provider);
  const busy = isLoading || isAddingApiToken;

  return (
    <div className="space-y-5">
      <div className="flex flex-col items-center text-center">
        <div className="mb-4">
          <ProviderMark provider={provider} />
        </div>
        <h3 className="text-foreground text-lg font-semibold tracking-tight">Before You Proceed</h3>
        <p className="text-muted-foreground mt-1 max-w-sm text-sm leading-5">
          Please ensure your {providerName} account has read & edit permissions for all{' '}
          {providerName} projects you’d like to connect with.
        </p>
      </div>
      <div className="flex flex-col gap-2">
        <Button
          isLoading={isLoading}
          label="Login"
          className="w-full"
          size="sm"
          onClick={() => onClickForOAuth()}
        />
        <Button
          label="Cancel"
          variant="outline"
          size="sm"
          className="w-full"
          onClick={closeModal}
          disabled={busy}
        />
      </div>

      {provider === 'WRIKE' && (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="border-border/80 h-px flex-1 border-t" />
            <span className="text-muted-foreground text-xs">or</span>
            <div className="border-border/80 h-px flex-1 border-t" />
          </div>
          <Textarea
            value={token}
            onChange={(e) => setToken(e.target.value)}
            label="API Token"
            disabled={busy}
          />
          <Button
            isLoading={isAddingApiToken}
            label="Save token"
            className="w-full"
            size="sm"
            disabled={!token.trim() || busy}
            onClick={() => {
              addApiToken(
                {
                  apiToken: token,
                  provider,
                },
                {
                  onError: serverError,
                  onSuccess: () => {
                    moveToProjectSelection();
                    utils.oAuth.getProviderConnectedStatus.invalidate();
                  },
                },
              );
            }}
          />
        </div>
      )}
    </div>
  );
};

export default function TaskManagerModal({
  provider,
  modalRef,
  closeModal,
  initialStep,
  skipProjectSelection,
  taskManagerType,
}: TaskManagerModalProps) {
  const [step, setStep] = useState<OAuthSteps>(initialStep);
  useEffect(() => {
    setStep(initialStep);
  }, [initialStep]);
  const [externalPopup, setExternalPopup] = useState<Window | null>(null);
  const [toggleForProjects, setToggleForSmartsheet] = useState<SmartsheetProjectTypes>(
    SmartsheetProjectTypes.SHEETS,
  );

  const utils = api.useUtils();

  const [projects, setProjects] = useState<{ key: string; name: string; disabled: boolean }[]>([]);
  const [selectedProject, setSelectedProject] = useState<string>('');

  const { isLoading: isProjectListLoading, mutate: getProjects } =
    api.task.listProjects.useMutation();

  const { isLoading: isConnectingToProjectLoading, mutate: connectProject } =
    api.task.syncTaskManagerById.useMutation();
  const [fieldMapping, setFieldMapping] = useState<Record<string, string>>();
  const [possibleColumns, setAllPossibleColumns] = useState<{ id: string; title: string }[]>([]);

  useEffect(() => {
    if (provider && !skipProjectSelection) {
      getProjects(
        { provider, type: toggleForProjects },
        {
          onSuccess: ({ projects: projectList, existingProjects }) => {
            setProjects(
              projectList.map((p) => ({
                ...p,
                disabled: !!existingProjects.find((t) => t.remoteProjectId === p.key),
              })),
            );
          },
          onError: serverError,
        },
      );
    }
  }, [getProjects, provider, toggleForProjects, skipProjectSelection]);

  const moveToProjectSelection = () => {
    setStep(OAuthSteps.PROJECT_SELECTION);
    getProjects(
      { provider: provider!, type: toggleForProjects },
      {
        onSuccess: ({ projects: projectList, existingProjects }) => {
          setProjects(
            projectList.map((p) => ({
              ...p,
              disabled: !!existingProjects.find((t) => t.remoteProjectId === p.key),
            })),
          );
        },
        onError: serverError,
      },
    );
  };

  useExternalPopup(externalPopup, setExternalPopup, provider!, () => {
    if (skipProjectSelection) {
      closeModal();
    } else {
      moveToProjectSelection();
    }
  });

  const finishSmartsheetConnect = () => {
    connectProject(
      {
        provider: provider!,
        remoteProjectId: selectedProject,
        type: toggleForProjects,
        fieldMapping,
        taskManagerType,
      },
      {
        onError: serverError,
        onSuccess: () => {
          closeModal();
          utils.task.getProjects.invalidate({ type: taskManagerType });
          toast.success(
            `Successfully synced to ${projects.find((p) => p.key === selectedProject)?.name}`,
          );
        },
      },
    );
  };

  const getComponentAsPerStep = () => {
    const components: Record<OAuthSteps, React.ReactElement<any>> = {
      [OAuthSteps.ALLOW_ACCESS]: (
        <AllowAccessStep<OAuthSteps>
          provider={provider!}
          permissions={permissions}
          setStep={setStep}
          nextStep={OAuthSteps.BEFORE_YOU_PROCEED}
          icon={provider ? <ProviderMark provider={provider} /> : undefined}
        />
      ),
      [OAuthSteps.BEFORE_YOU_PROCEED]: (
        <BeforeYouProceed
          setExternalPopup={setExternalPopup}
          provider={provider}
          moveToProjectSelection={moveToProjectSelection}
          closeModal={closeModal}
        />
      ),
      [OAuthSteps.PROJECT_SELECTION]: (
        <ProjectSelection
          provider={provider}
          projects={projects}
          closeModal={closeModal}
          isProjectListLoading={isProjectListLoading}
          selectedProject={selectedProject}
          setSelectedProject={setSelectedProject}
          toggleForProjects={toggleForProjects}
          setToggleForSmartsheet={setToggleForSmartsheet}
          setAllPossibleColumns={setAllPossibleColumns}
          setFieldMapping={setFieldMapping}
          setStep={setStep}
          taskManagerType={taskManagerType}
        />
      ),
      [OAuthSteps.FIELD_MAPPING]: (
        <SmartsheetFieldMappingStep
          columns={possibleColumns}
          mapping={{
            'Task ID': fieldMapping?.['Task ID'] ?? '',
            Name: fieldMapping?.Name ?? '',
            Description: fieldMapping?.Description ?? '',
            Status: fieldMapping?.Status ?? '',
          }}
          onMappingChange={(next) => setFieldMapping(next)}
          onContinue={finishSmartsheetConnect}
          onBack={() => setStep(OAuthSteps.PROJECT_SELECTION)}
          isContinuing={isConnectingToProjectLoading}
        />
      ),
    };
    return components[step];
  };

  return (
    <Modal
      ref={modalRef}
      open={!!provider}
      className={step === OAuthSteps.FIELD_MAPPING ? 'max-w-lg' : 'max-w-[26rem]'}
      onClose={() => setTimeout(() => setStep(initialStep), 1000)}
    >
      {getComponentAsPerStep()}
    </Modal>
  );
}
